import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Kicker, SectionEyebrow, SectionTitle } from "./Components/common/Section";
import releasesData, { Release } from "../data/releases";

const Page = styled.section`
  overflow: clip;
`;

const HeroSection = styled.div`
  position: relative;
  padding: clamp(7rem, 12vw, 10rem) 8vw 4rem;

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
  background:
    radial-gradient(circle at 70% 15%, rgba(214, 179, 106, 0.14), transparent 28%),
    linear-gradient(180deg, rgba(11, 11, 13, 0.92), rgba(5, 5, 6, 1));

  &::after {
    content: "";
    position: absolute;
    inset: auto 8vw 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--ink-12), transparent);
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeroTitle = styled.h1`
  font-family: "DM Serif Display", serif;
  font-size: clamp(3rem, 7vw, 5.5rem);
  line-height: 0.92;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
`;

const HeroText = styled.p`
  max-width: 48rem;
  color: var(--ink-78);
  line-height: 1.85;
  font-size: clamp(1rem, 1.4vw, 1.12rem);
`;

const TagStrip = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const Tag = styled.span`
  padding: 8px 14px;
  border: 1px solid var(--ink-12);
  background: rgba(255, 255, 255, 0.03);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink);
`;

const CatalogSection = styled.section`
  padding: clamp(3.5rem, 6vw, 5rem) 8vw;

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 22px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
`;

const GridItem = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid var(--ink-08);
  background:
    radial-gradient(circle at top, rgba(214, 179, 106, 0.06), transparent 40%),
    rgba(255, 255, 255, 0.02);
  transition: transform 0.28s ease, border-color 0.28s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(214, 179, 106, 0.4);

    img {
      transform: scale(1.03);
    }
  }
`;

const ArtworkWrap = styled.div`
  aspect-ratio: 1 / 1;
  overflow: hidden;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.02);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    padding: 12px;
    transition: transform 0.36s ease;
  }
`;

const ItemMeta = styled.div`
  padding: 16px 18px;
  border-top: 1px solid var(--ink-06);

  h3 {
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink);
    margin-bottom: 4px;
  }

  p {
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.06em;
  }
`;

interface CatalogItem {
  id: string;
  title: string;
  artist?: string;
  imageUrl: string;
  genre?: string;
  stat?: string;
}

const ImageCard: React.FC<Omit<CatalogItem, "id">> = ({ title, artist, imageUrl, genre, stat }) => (
  <GridItem>
    <ArtworkWrap>
      <img loading="lazy" src={imageUrl} alt={title} />
    </ArtworkWrap>
    <ItemMeta>
      <h3>{title}</h3>
      {artist && <p style={{ color: "var(--ink)", marginBottom: 2 }}>{artist}</p>}
      {genre && <p>{genre}</p>}
      {stat && <p style={{ color: "var(--accent)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" as const }}>{stat}</p>}
    </ItemMeta>
  </GridItem>
);

const FilterBar = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 28px;
`;

const FilterBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${(p) => p.$active ? "rgba(214,179,106,.6)" : "var(--ink-12)"};
  background: ${(p) => p.$active ? "rgba(214,179,106,.12)" : "rgba(255,255,255,.03)"};
  color: ${(p) => p.$active ? "var(--accent)" : "var(--ink)"};
  font-size: 11px; letter-spacing: .12em; text-transform: uppercase;
  cursor: pointer;
  transition: all .2s ease;
  &:hover { border-color: rgba(214,179,106,.4); }
`;

const genreFromMeta = (meta?: string): string => {
  if (!meta) return "Other";
  const lower = meta.toLowerCase();
  if (lower.includes("afro")) return "Afrobeats";
  if (lower.includes("pop")) return "Pop";
  if (lower.includes("remix")) return "Remix";
  if (lower.includes("dance")) return "Afrobeats";
  if (lower.includes("ep")) return "EP";
  return "Other";
};

const ALL_FILTER = "All";

const Music: React.FC = () => {
  const [filter, setFilter] = useState(ALL_FILTER);

  const allItems: CatalogItem[] = useMemo(() =>
    [...releasesData]
      .sort((a, b) => b.streams - a.streams)
      .map((r) => ({
        id: r.id,
        title: r.title,
        artist: r.artist,
        imageUrl: r.image,
        genre: r.meta,
        stat: r.stat,
      })),
    []
  );

  const genres = useMemo(() => {
    const set = new Set(allItems.map((i) => genreFromMeta(i.genre)));
    return [ALL_FILTER, ...Array.from(set).sort()];
  }, [allItems]);

  const filtered = filter === ALL_FILTER
    ? allItems
    : allItems.filter((i) => genreFromMeta(i.genre) === filter);

  return (
    <Page>
      <Helmet>
        <title>
          Music Catalog — RMS | Signature Releases & Global Collaborations
        </title>
        <meta
          name="description"
          content="Explore RMS catalog: Skelewu, Peru, original compositions, and global releases. 1B+ streams across Afrobeats, Pop, and more."
        />
        <meta property="og:title" content="Music Catalog — RMS" />
        <meta
          property="og:description"
          content="Signature releases and global collaborations featuring our best work."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <meta property="og:url" content="https://rmsociete.com/music" />
        <meta property="og:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <link rel="canonical" href="https://rmsociete.com/music" />
        <meta
          name="keywords"
          content="music catalog, Skelewu, Peru, Afrobeats, streaming, releases"
        />
      </Helmet>

      <HeroSection>
        <HeroGrid>
          <div>
            <Kicker>Catalog</Kicker>
            <HeroTitle>Signature releases and global collaborations.</HeroTitle>
            <HeroText>
              A curated selection of RMS catalog highlights. Each release blends
              cultural storytelling with chart-ready production and publishing-minded
              execution.
            </HeroText>
          </div>
          <TagStrip>
            <Tag>Afro Beats</Tag>
            <Tag>Reggaeton</Tag>
            <Tag>Pop</Tag>
            <Tag>Global Anthems</Tag>
          </TagStrip>
        </HeroGrid>
      </HeroSection>

      <CatalogSection>
        <SectionEyebrow>Selected work</SectionEyebrow>
        <SectionTitle>From the RMS orbit.</SectionTitle>
        <FilterBar>
          {genres.map((g) => (
            <FilterBtn key={g} $active={filter === g} onClick={() => setFilter(g)}>
              {g}
            </FilterBtn>
          ))}
        </FilterBar>
        <Grid>
          {filtered.map((item) => (
            <ImageCard
              key={item.id}
              title={item.title}
              artist={item.artist}
              imageUrl={item.imageUrl}
              genre={item.genre}
              stat={item.stat}
            />
          ))}
        </Grid>
      </CatalogSection>
    </Page>
  );
};

export default Music;
